import React, { useMemo, useState } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { Text } from '@/components/common/text';
import { IEvent, CALENDAR_KEYS } from '@/types/admin/events';
import { EMPTY_MESSAGES } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { formatEventType } from '@/hooks/admin/useEvents';
import { DesktopPanelTabs } from '@/components/common/admin/desktop/panel/DesktopPanelTabs';
import { DesktopPanelBody } from '@/components/common/admin/desktop/panel/DesktopPanelBody';
import { EventInfoTab } from '../EventInfoTab';
import { EventAttendanceTab } from '../AttendanceTab';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';

interface DesktopEventDetailsPanelProps {
  event: IEvent | null;
  onEdit?: (eventId: number) => void;
}

export const DesktopEventDetailsPanel: React.FC<DesktopEventDetailsPanelProps> = ({
  event,
  onEdit,
}) => {
  const { redesign, admin } = useAdminColors();
  const [tabIndex, setTabIndex] = useState(0);

  const eventType = useMemo(() => (event ? formatEventType(event) : null), [event]);
  const eventKey = useMemo(
    () => (eventType ? CALENDAR_KEYS.find((k) => k.title === eventType) : null),
    [eventType],
  );

  if (!event) {
    return (
      <GlassCard
        padding={0}
        borderRadius={12}
        variant="strong"
        style={[styles.container, styles.emptyState]}
      >
        {(() => {
          const iconKey = EMPTY_MESSAGES.events.desktopNoEvents.icon;
          const IconComponent = SVG_COMPONENTS[iconKey];
          if (!IconComponent) return null;
          return (
            <IconComponent
              width={96}
              height={96}
              color={redesign.text.subtle}
              style={styles.emptyIcon}
            />
          );
        })()}
        <Text style={[styles.emptyTitle, { color: redesign.text.primary }]}>
          {EMPTY_MESSAGES.events.desktopNoEvents.title}
        </Text>
        <Text style={[styles.emptySubtitle, { color: redesign.text.muted }]}>
          {EMPTY_MESSAGES.events.desktopNoEvents.message}
        </Text>
      </GlassCard>
    );
  }

  return (
    <GlassCard padding={0} borderRadius={12} variant="strong" style={styles.container}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: redesign.card.border }]}>
        <View style={styles.headerLeft}>
          <Text style={[styles.title, { color: redesign.text.primary }]} numberOfLines={2}>
            {event.name}
          </Text>
          <View style={styles.statusRow}>
            <View
              style={[
                styles.statusDot,
                { backgroundColor: eventKey?.color || redesign.text.muted },
              ]}
            />
            <Text style={[styles.statusText, { color: redesign.text.muted }]}>
              {eventType}
            </Text>
          </View>
        </View>
        {onEdit && (
          <TouchableOpacity
            style={[styles.editButton, { backgroundColor: admin.formInput.borderFocused }]}
            onPress={() => onEdit(event.id)}
          >
            <Text style={styles.editButtonText}>Edit</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Tabs */}
      <DesktopPanelTabs
        tabs={['Event Info', 'Attendees']}
        activeIndex={tabIndex}
        onChange={setTabIndex}
      />

      {/* Content */}
      <DesktopPanelBody scroll>
        {tabIndex === 0 && <EventInfoTab event={event} />}
        {tabIndex === 1 && <EventAttendanceTab event={event} />}
      </DesktopPanelBody>
    </GlassCard>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  emptyIcon: {
    marginBottom: 12,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  headerLeft: {
    flex: 1,
    marginRight: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 6,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statusText: {
    fontSize: 13,
  },
  editButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
  },
  editButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
});
